import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { hasHousemateDetails } from './HousemateDetailsModule/housemateDetailsModule.utils';
import { hasNearbySchoolsData } from './NearbySchoolsModule/schoolUtils';
import { hasRecommendationSource } from './RecommendationListingsModule/recommendationListings.utils';
import { hasSourceInfoData } from './SourceInfoModule/sourceInfoModule.utils';

export type ModuleType =
  | 'listing-summary'
  | 'tour-confirmation'
  | 'amenities'
  | 'neighborhood'
  | 'nearby-schools'
  | 'about-building'
  | 'housemate-details'
  | 'pets'
  | 'competition'
  | 'open-houses'
  | 'units'
  | 'property-manager'
  | 'cost-and-fees'
  | 'pricing-comparison'
  | 'inline-contact'
  | 'listing-feedback'
  | 'similar-listings'
  | 'provider-listings'
  | 'most-popular-listings'
  | 'pet-friendly-listings'
  | 'recently-viewed'
  | 'source-info'
  | 'legal-info'
  | 'faq';

type ListingSnapshot = PropertyDetailListing | null | undefined;

export const canRetainData = (listing: ListingSnapshot): boolean =>
  listing?.active !== false || listing?.isAllowedToRetain !== false;

export const isInactiveListing = (listing: ListingSnapshot): boolean => listing?.active === false;

export const LISTING_SUMMARY = 'listing-summary' as const;
export const TOUR_CONFIRMATION = 'tour-confirmation' as const;
export const AMENITIES = 'amenities' as const;
export const NEIGHBORHOOD = 'neighborhood' as const;
export const NEARBY_SCHOOLS = 'nearby-schools' as const;
export const ABOUT_BUILDING = 'about-building' as const;
export const HOUSEMATE_DETAILS = 'housemate-details' as const;
export const PETS = 'pets' as const;
export const COMPETITION = 'competition' as const;
export const OPEN_HOUSES = 'open-houses' as const;
export const UNITS = 'units' as const;
export const PROPERTY_MANAGER = 'property-manager' as const;
export const COST_AND_FEES = 'cost-and-fees' as const;
export const PRICING_COMPARISON = 'pricing-comparison' as const;
export const INLINE_CONTACT = 'inline-contact' as const;
export const LISTING_FEEDBACK = 'listing-feedback' as const;
export const SIMILAR_LISTINGS = 'similar-listings' as const;
export const PROVIDER_LISTINGS = 'provider-listings' as const;
export const MOST_POPULAR_LISTINGS = 'most-popular-listings' as const;
export const PET_FRIENDLY_LISTINGS = 'pet-friendly-listings' as const;
export const RECENTLY_VIEWED = 'recently-viewed' as const;
export const SOURCE_INFO = 'source-info' as const;
export const LEGAL_INFO = 'legal-info' as const;
export const FAQ = 'faq' as const;

type PetPolicies = { dog?: unknown; cat?: unknown } | null | undefined;

type FloorplanSnapshot = {
  units?: Array<unknown>;
} | null;

const hasFloorplanOrUnitsSurface = (listing: ListingSnapshot): boolean => {
  if (!listing) return false;
  if (listing.isApartmentBldg === true || (listing.active !== false && listing.trusted === true)) return true;

  const floorplans = (listing as { floorplans?: Array<FloorplanSnapshot> }).floorplans ?? [];
  if (floorplans.length > 1) return true;

  return (floorplans[0]?.units?.length ?? 0) > 1;
};

export const shouldRenderUnitsModule = (listing: ListingSnapshot): boolean =>
  canRetainData(listing) && hasFloorplanOrUnitsSurface(listing);

const hasOpenHousesData = (listing: ListingSnapshot): boolean =>
  ((listing as { openHouses?: Array<unknown> } | null | undefined)?.openHouses ?? []).length > 0;

const hasLegalInfoData = (listing: ListingSnapshot): boolean =>
  Boolean((listing as { legalDisclaimer?: string } | null | undefined)?.legalDisclaimer?.trim());

const hasProviderListingsSource = (listing: ListingSnapshot): boolean => {
  const alias = (listing as { aliasEncoded?: string } | null | undefined)?.aliasEncoded;
  return canRetainData(listing) && Boolean(alias);
};

export type ModuleTitleKey =
  | 'tourConfirmationTitle'
  | 'amenitiesTitle'
  | 'neighborhoodTitle'
  | 'nearbySchoolsTitle'
  | 'aboutBuildingTitle'
  | 'housemateDetailsTitle'
  | 'petsTitle'
  | 'competitionTitle'
  | 'openHousesTitle'
  | 'unitsTitle'
  | 'propertyManagerTitle'
  | 'costAndFeesTitle'
  | 'pricingComparisonTitle'
  | 'similarListingsTitle'
  | 'providerListingsTitle'
  | 'mostPopularListingsTitle'
  | 'petFriendlyListingsTitle'
  | 'recentlyViewedTitle'
  | 'sourceInfoTitle'
  | 'legalInfoTitle'
  | 'faqTitle';

export type ModuleMetadata = {
  id: string;
  type: ModuleType;
  enabled?: boolean;
  /** English fallback. Renderers should prefer `titleKey` translated through `hdp.*`. */
  title?: string;
  titleKey?: ModuleTitleKey;
  shouldRender?: (listing: ListingSnapshot) => boolean;
  componentOwnsChrome?: boolean;
};

const moduleDefinitions: Record<ModuleType, Omit<ModuleMetadata, 'type'>> = {
  [LISTING_SUMMARY]: {
    id: 'listing-summary-module',
    enabled: true,
  },
  [TOUR_CONFIRMATION]: {
    id: 'tour-confirmation-module',
    enabled: true,
    title: 'Your booked tour',
    titleKey: 'tourConfirmationTitle',
    componentOwnsChrome: true,
  },
  [AMENITIES]: {
    id: 'amenities-module',
    enabled: true,
    title: 'What you get',
    titleKey: 'amenitiesTitle',
    shouldRender: canRetainData,
  },
  [NEIGHBORHOOD]: {
    id: 'neighborhood-module',
    enabled: true,
    title: 'The neighborhood',
    titleKey: 'neighborhoodTitle',
    // Hide for undisclosed addresses — the neighborhood map / commute tools
    // would reveal the surrounding streets and defeat the point of hiding
    // the address.
    shouldRender: (listing) =>
      (listing as { address?: { hideStreet?: boolean } } | null | undefined)?.address?.hideStreet !== true,
  },
  [NEARBY_SCHOOLS]: {
    id: 'nearby-schools-module',
    enabled: true,
    title: 'Nearby schools',
    titleKey: 'nearbySchoolsTitle',
    shouldRender: (listing) => canRetainData(listing) && hasNearbySchoolsData(listing),
  },
  [ABOUT_BUILDING]: {
    id: 'about-building-module',
    enabled: true,
    title: 'About the building',
    titleKey: 'aboutBuildingTitle',
    shouldRender: canRetainData,
  },
  [HOUSEMATE_DETAILS]: {
    id: 'housemate-details-module',
    enabled: true,
    title: 'Housemate details',
    titleKey: 'housemateDetailsTitle',
    shouldRender: (listing) => canRetainData(listing) && hasHousemateDetails(listing),
  },
  [PETS]: {
    id: 'pets-module',
    enabled: true,
    title: 'Pets',
    titleKey: 'petsTitle',
    componentOwnsChrome: true,
    shouldRender: (listing) => {
      if (!canRetainData(listing)) return false;
      const petPolicies = (listing?.amenities as { petPolicies?: PetPolicies } | undefined)?.petPolicies;
      return petPolicies != null && (petPolicies.dog != null || petPolicies.cat != null);
    },
  },
  [COMPETITION]: {
    id: 'competition-module',
    enabled: true,
    title: 'Competition for this rental',
    titleKey: 'competitionTitle',
    shouldRender: (listing) => {
      if (!canRetainData(listing)) return false;
      return (
        ((listing as { popularity?: { summary?: { viewed?: number } } } | null | undefined)?.popularity?.summary
          ?.viewed ?? 0) > 0
      );
    },
  },
  [OPEN_HOUSES]: {
    id: 'open-houses-module',
    enabled: true,
    title: 'Open houses',
    titleKey: 'openHousesTitle',
    shouldRender: (listing) => canRetainData(listing) && hasOpenHousesData(listing),
  },
  [UNITS]: {
    id: 'units-module',
    enabled: true,
    title: 'Units available in this building',
    titleKey: 'unitsTitle',
    shouldRender: shouldRenderUnitsModule,
  },
  [PROPERTY_MANAGER]: {
    id: 'property-manager-module',
    enabled: true,
    title: 'The property manager',
    titleKey: 'propertyManagerTitle',
    shouldRender: canRetainData,
  },
  [COST_AND_FEES]: {
    id: 'cost-and-fees-module',
    enabled: true,
    title: 'Costs, fees, and lease terms',
    titleKey: 'costAndFeesTitle',
    shouldRender: canRetainData,
  },
  [PRICING_COMPARISON]: {
    id: 'pricing-comparison-module',
    enabled: true,
    title: 'Pricing comparison',
    titleKey: 'pricingComparisonTitle',
    shouldRender: (listing) => {
      if (!canRetainData(listing)) return false;
      if (listing?.showPricingStats !== true) return false;
      if (listing.isApartmentBldg === true) return false;
      const stats = listing.areas?.surroundingAreaStats;
      return Boolean(stats?.neighborhood || stats?.city || stats?.county);
    },
  },
  [INLINE_CONTACT]: {
    id: 'inline-contact-module',
    enabled: true,
    shouldRender: canRetainData,
  },
  [LISTING_FEEDBACK]: {
    id: 'listing-feedback-module',
    enabled: true,
  },
  [SIMILAR_LISTINGS]: {
    id: 'similar-listings-module',
    enabled: true,
    title: 'Find similar homes',
    titleKey: 'similarListingsTitle',
  },
  [PROVIDER_LISTINGS]: {
    id: 'provider-listings-module',
    enabled: true,
    title: 'More from this provider',
    titleKey: 'providerListingsTitle',
    shouldRender: hasProviderListingsSource,
  },
  [MOST_POPULAR_LISTINGS]: {
    id: 'most-popular-listings-module',
    enabled: true,
    title: 'Most popular nearby',
    titleKey: 'mostPopularListingsTitle',
    shouldRender: (listing) => canRetainData(listing) && hasRecommendationSource(listing),
    componentOwnsChrome: true,
  },
  [PET_FRIENDLY_LISTINGS]: {
    id: 'pet-friendly-listings-module',
    enabled: true,
    title: 'Pet-friendly rentals nearby',
    titleKey: 'petFriendlyListingsTitle',
    shouldRender: (listing) => canRetainData(listing) && hasRecommendationSource(listing),
    componentOwnsChrome: true,
  },
  [RECENTLY_VIEWED]: {
    id: 'recently-viewed-module',
    enabled: true,
    title: 'Recently viewed homes',
    titleKey: 'recentlyViewedTitle',
  },
  [SOURCE_INFO]: {
    id: 'source-info-module',
    enabled: true,
    title: 'Listing source',
    titleKey: 'sourceInfoTitle',
    shouldRender: (listing) => canRetainData(listing) && hasSourceInfoData(listing),
  },
  [LEGAL_INFO]: {
    id: 'legal-info-module',
    enabled: true,
    title: 'Legal info',
    titleKey: 'legalInfoTitle',
    shouldRender: (listing) => canRetainData(listing) && hasLegalInfoData(listing),
  },
  [FAQ]: {
    id: 'faq-module',
    enabled: true,
    title: 'Frequently asked questions',
    titleKey: 'faqTitle',
    shouldRender: (listing) => listing != null && listing.active !== false && listing.trusted === true,
  },
};

export const moduleOrder: Array<ModuleType> = [
  LISTING_SUMMARY,
  TOUR_CONFIRMATION,
  AMENITIES,
  UNITS,
  COST_AND_FEES,
  PRICING_COMPARISON,
  PETS,
  COMPETITION,
  OPEN_HOUSES,
  NEIGHBORHOOD,
  ABOUT_BUILDING,
  HOUSEMATE_DETAILS,
  PROPERTY_MANAGER,
  NEARBY_SCHOOLS,
  INLINE_CONTACT,
  SIMILAR_LISTINGS,
  PROVIDER_LISTINGS,
  MOST_POPULAR_LISTINGS,
  PET_FRIENDLY_LISTINGS,
  RECENTLY_VIEWED,
  LEGAL_INFO,
  SOURCE_INFO,
  FAQ,
  LISTING_FEEDBACK,
];

export const moduleMetadata: Array<ModuleMetadata> = moduleOrder.map((type) => ({
  type,
  ...moduleDefinitions[type],
}));
